import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ShieldCheck, ShieldOff, KeyRound, MonitorX } from "lucide-react";
import { TwoFactorSetupDialog, BackupCodesDialog, ReauthDialog } from "@/components/TwoFactorDialogs";

/** Settings card: owner-only authenticator-app two-step login. */
export function TwoFactorCard() {
    const [status, setStatus] = useState(null);
    const [setupOpen, setSetupOpen] = useState(false);
    const [reauth, setReauth] = useState(null); // "disable" | "codes"
    const [codes, setCodes] = useState(null);

    const load = useCallback(async () => {
        try { setStatus((await api.get("/auth/2fa/status")).data); } catch { setStatus(null); }
    }, []);
    useEffect(() => { load(); }, [load]);

    if (!status?.available) return null;

    const onReauth = async (password, code) => {
        if (reauth === "disable") {
            await api.post("/auth/2fa/disable", { password, code });
            toast.success("Two-step login turned off");
        } else {
            const { data } = await api.post("/auth/2fa/backup-codes", { password, code });
            setCodes(data.backup_codes);
        }
        setReauth(null);
        load();
    };

    const revoke = async () => {
        if (!window.confirm("Forget all trusted devices? Every browser will need a code next time.")) return;
        await api.post("/auth/2fa/trusted-devices/revoke");
        toast.success("Trusted devices cleared");
        load();
    };

    return (
        <Card className="lg:col-span-2" data-testid="twofa-card">
            <CardContent className="p-6 space-y-4">
                <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                        {status.enabled ? <ShieldCheck className="w-5 h-5 text-emerald-600 mt-0.5" />
                            : <ShieldOff className="w-5 h-5 text-amber-600 mt-0.5" />}
                        <div>
                            <div className="font-heading font-semibold">Two-step login (owner account)</div>
                            <div className="text-xs text-muted-foreground mt-0.5" data-testid="twofa-status-text">
                                {status.enabled
                                    ? `On · ${status.backup_codes_remaining} backup codes left · ${status.trusted_devices} trusted device${status.trusted_devices === 1 ? "" : "s"}`
                                    : "Off · protect your owner account with a code from an authenticator app"}
                            </div>
                        </div>
                    </div>
                    {!status.enabled && (
                        <Button size="sm" onClick={() => setSetupOpen(true)} data-testid="twofa-enable-btn">Turn on</Button>
                    )}
                </div>
                {status.enabled && (
                    <div className="flex flex-wrap gap-2">
                        <Button size="sm" variant="outline" onClick={() => setReauth("codes")} data-testid="twofa-regen-btn">
                            <KeyRound className="w-4 h-4 mr-1" />New backup codes
                        </Button>
                        <Button size="sm" variant="outline" onClick={revoke} disabled={!status.trusted_devices}
                                data-testid="twofa-revoke-btn">
                            <MonitorX className="w-4 h-4 mr-1" />Forget trusted devices
                        </Button>
                        <Button size="sm" variant="outline" className="text-red-600" onClick={() => setReauth("disable")}
                                data-testid="twofa-disable-btn">
                            Turn off
                        </Button>
                    </div>
                )}
                {status.enabled && status.backup_codes_remaining <= 3 && (
                    <div className="text-xs text-amber-700 dark:text-amber-400" data-testid="twofa-low-codes">
                        You're running low on backup codes. Generate new ones.
                    </div>
                )}
            </CardContent>
            <TwoFactorSetupDialog open={setupOpen} setOpen={setSetupOpen}
                                  onEnabled={(c) => { setCodes(c); load(); }} />
            <ReauthDialog mode={reauth} onClose={() => setReauth(null)} onSubmit={onReauth} />
            <BackupCodesDialog codes={codes} onClose={() => setCodes(null)} />
        </Card>
    );
}
